import { memo, useCallback, useEffect, useState } from 'react'
import {
  Avatar,
  CircularProgress,
  Divider,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import MuiIconButton from '@mui/material/IconButton'
import { getFileUrl } from '@weblite/file'
import { useDisplayedConnectionStatus } from '$comp/main/react-hook'
import {
  openRoomListSearch,
  openSelectWorkspaceName,
  switchToRoomList,
} from '$logic/router'
import {
  APP_BAR_HEIGHT_CSS_VAR,
  CHAT_LIST_APP_BAR_HEIGHT,
} from '$common/app-bar/app-bar.constants'
import { SHOULD_CATEGORIZE_CHATS } from '$func/environmentInfo'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { resetWorkspace, useCurrentWorkspace } from '$logic/workspace'
import { DEFAULT_TRANSITION_MS } from '$comp/_app/components/story/constants'
import { chatAppbarIsExpandedDefaultValue } from './chat-app-bar-api'
import { ArrowForward, Groups, Search, Settings } from '@mui/icons-material'
import { AVATAR_SIZES } from '$common/avatar/avatar-base/conf'
import { chatAppbarCallbackManager } from './chat-app-bar'
import { useClientUser } from '$logic/client-user'

export const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  height: APP_BAR_HEIGHT_CSS_VAR,
  position: 'relative',
  transition: `height ${DEFAULT_TRANSITION_MS}ms`,
})

export const Container = styled('div')(({ expanded }) => ({
  display: 'flex',
  height: expanded ? '132px' : '56px',
  transition: `height ${DEFAULT_TRANSITION_MS}ms`,
  alignItems: expanded ? 'start' : 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  zIndex: 2,
  gap: '8px',
  padding: '8px',
}))

const ConnectionStatusWrapper = styled('div')`
  display: flex;
  gap: 8px;
  margin-right: 8px;
  color: ${({ theme }) => theme.palette.secondary700};
`

const IconButton = styled(MuiIconButton)`
  color: ${({ theme }) => theme.palette.neutral000};
`

export const WorkspaceAppBar = memo(function ChatAppBarComponent() {
  const connectionStatus = useDisplayedConnectionStatus()

  const onSearchIcon = () => {
    openRoomListSearch()
    analite('CHATLIST_SEARCH_CLICK')
  }

  const onBackIcon = () => {
    resetWorkspace()
    switchToRoomList()
  }

  const currentWorkspace = useCurrentWorkspace()
  // useCurrentWorkSpace can return undefined but useCurrentWorkSpaceId() will return DEFAULT_WORKSPACE_ID if no workspace is selected

  const clientUserId = useClientUser().id
  const isAdmin = currentWorkspace?.ownerId === clientUserId

  const [isExpanded, setIsExpanded] = useState(chatAppbarIsExpandedDefaultValue)

  const setIsExpandedCallback = useCallback((newExpandedValue: boolean) => {
    setIsExpanded(newExpandedValue)
  }, [])

  useEffect(() => {
    chatAppbarCallbackManager.addCallback(setIsExpandedCallback)
    return () => chatAppbarCallbackManager.removeCallback(setIsExpandedCallback)
  }, [])

  const theme = useTheme()

  return (
    <Root expanded={isExpanded}>
      <Container
        expanded={isExpanded}
        style={{ backgroundColor: currentWorkspace?.color }}
      >
        <IconButton onClick={onBackIcon}>
          <ArrowForward />
        </IconButton>

        {isExpanded ? null : connectionStatus ? (
          <ConnectionStatusBox
            connectionStatus={connectionStatus}
            color="neutral000"
            variant="subtitle1"
          />
        ) : (
          <Typography
            variant="subtitle1"
            color="neutral000"
            marginLeft="auto"
            alignSelf="center"
          >
            {currentWorkspace?.name}
          </Typography>
        )}

        <div
          style={{
            display: 'flex',
            gap: '4px',
            marginLeft: isExpanded ? 0 : '44px',
            transition: `margin-left ${DEFAULT_TRANSITION_MS}ms`,
          }}
        >
          {isAdmin && (
            <IconButton
              onClick={() => openSelectWorkspaceName(currentWorkspace?.id)}
            >
              <Settings />
            </IconButton>
          )}
          <IconButton onClick={onSearchIcon}>
            <Search />
          </IconButton>
        </div>
      </Container>

      {isExpanded && (
        <>
          <div
            style={{
              height: '82px',
              position: 'relative',
              padding: '20px',
              backgroundColor: theme.palette.neutral000,
              display: 'flex',
              // transition: `height ${DEFAULT_TRANSITION_MS}ms`,
              gap: '8px',
            }}
          >
            {connectionStatus ? (
              <ConnectionStatusBox connectionStatus={connectionStatus} />
            ) : (
              <>
                <Groups />
                <Typography variant="h2" color="neutral1000">
                  {currentWorkspace?.name}
                </Typography>
              </>
            )}
          </div>
          <Divider />
        </>
      )}

      <Avatar
        src={getFileUrl(currentWorkspace?.icon || '')}
        sx={{
          border: isExpanded ? '6px solid' : 'none',
          borderColor: `neutral000`,
          borderRadius: isExpanded ? '12px' : '6px',
          position: 'absolute',
          top: isExpanded ? '96px' : '12px',
          left: isExpanded ? '14px' : '12px',
          transition: `all ${DEFAULT_TRANSITION_MS}ms`,
          zIndex: 3,
          width: isExpanded
            ? AVATAR_SIZES[AvatarSize.XL]
            : AVATAR_SIZES[AvatarSize.XS],
          height: isExpanded
            ? AVATAR_SIZES[AvatarSize.XL]
            : AVATAR_SIZES[AvatarSize.XS],
        }}
      >
        <Groups />
      </Avatar>
    </Root>
  )
})

export const ChatAppBarRoot = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 8px;
  box-sizing: border-box;
  height: ${CHAT_LIST_APP_BAR_HEIGHT}px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  border-bottom: ${({ theme }) =>
    SHOULD_CATEGORIZE_CHATS ? 'none' : `1px solid ${theme.palette.neutral200}`};
  transition: height ${DEFAULT_TRANSITION_MS}ms;
`

export function ConnectionStatusBox({
  connectionStatus,
  color = 'neutral900',
  variant = 'h2',
}: {
  connectionStatus: string
  color?: string
  variant?: string
}) {
  return (
    <ConnectionStatusWrapper>
      <CircularProgress
        sx={{ color }}
        size={variant === 'subtitle1' ? 16 : 24}
      />
      <Typography color={color} variant={variant}>
        {connectionStatus}
      </Typography>
    </ConnectionStatusWrapper>
  )
}
